import { AxisTickProps } from '@nivo/axes'
import { Bar, BarDatum, ComputedDatum } from '@nivo/bar'
import { DSTooltip } from '@repo/design-system/index'
import { QIcon_question_outline } from '@repo/icons/index'
import produce from 'immer'
import { useRef, useState } from 'react'
import { CAF_ANALYSIS_TYPE_MAP } from '../../../../model/caf.interface'
import {
  ACTIVE_CHART_COLOR,
  GRAPH_TOOLTIP_CLS,
  HIGH_ACTIVE_CHART_COLOR,
  HIGH_INACTIVE_CHART_COLOR,
  INACTIVE_CHART_COLOR,
  chartWidth,
  graph_common_props,
} from '../../../../modules/consts/caf'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import {
  axisRange,
  maxAxisRange,
  useRollbackChartColor,
  useScrollPosition,
} from '../../../../modules/hooks/webview/caf/chart/utilFunction'
import { s_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ICON_QUESTION_GRAY } from '../../../../modules/svg/svg'
import { COLORS } from '../../../../modules/vars'
import { useWebviewModal } from '../common/Modal/WebviewModal'
import { CustomXTick, CustomYTick } from './common/CustomXYTick'

interface IBarChartProps {
  data: any
  dataKey?: string
  indexBy?: string
  type?: string
}
interface BarChartData extends BarDatum {
  turn: number
  color: string
  ngram_ratio?: number
  mlr?: number
}

const HIGH_STANDARD_NUM = 3

const createDataWithColor = (originalData = [], dataKey, type) => {
  const dataColor = originalData?.map((d, index) => {
    return {
      ['turn']: index + 1,
      [dataKey]: Number(d[dataKey]).toFixed(2),
      color:
        type === 'fluency'
          ? ACTIVE_CHART_COLOR
          : Math.floor(d[dataKey]) <= HIGH_STANDARD_NUM
          ? HIGH_ACTIVE_CHART_COLOR
          : ACTIVE_CHART_COLOR,
    }
  })
  return dataColor
}

const BarChart = ({ data, dataKey, type = '' }: IBarChartProps) => {
  const scrollRef = useRef(null)
  const [scrollX] = useScrollPosition(scrollRef)
  const isMobile = useIsMobile()

  const { t } = useTranslation()

  const rollbackChartColor = useRollbackChartColor()

  /**
   * 서버 응답 데이터에 그래프 스타일링 추가
   * */
  const [graphData, setGraphData] = useState<BarChartData[]>(createDataWithColor(data, dataKey, type))

  const handleClickBarItem = (e) => {
    setGraphData(
      produce(graphData, (draft) => {
        const findIndex = draft.findIndex((item) => item.turn === e?.data.turn)

        //다른 바의 색상은 inactive
        if (type === 'fluency') {
          draft?.map((item) => (item.color = INACTIVE_CHART_COLOR))
          // 선택된 바의 색상 active
          if (findIndex !== -1) {
            const targetBar = draft[findIndex]
            targetBar.color = ACTIVE_CHART_COLOR
          }
        } else {
          if (findIndex !== -1) {
            draft?.map(
              (item) =>
                (item.color =
                  Number(item[dataKey]) <= HIGH_STANDARD_NUM ? HIGH_INACTIVE_CHART_COLOR : INACTIVE_CHART_COLOR)
            )
            const targetBar = draft[findIndex]
            targetBar.color =
              Number(targetBar[dataKey]) <= HIGH_STANDARD_NUM ? HIGH_ACTIVE_CHART_COLOR : ACTIVE_CHART_COLOR
          }
        }
      })
    )
  }
  const webviewPopup = useWebviewModal()
  const handleClickTooltip = () => {
    webviewPopup.show({
      id: 'epc_popup',
      title: t(s_caf.graph_common.user_section.modal_title),
      body: t(s_caf.graph_common.user_section.modal_subtitle),
    })
  }

  const xRange = axisRange(graphData.length, 5, 1).slice(1)
  const yRange = axisRange(maxAxisRange(graphData, dataKey), 5, 1)
  const setChartColor = (item) => {
    if (type === 'fluency') item.color = ACTIVE_CHART_COLOR
    else {
      return (item.color = item[dataKey] <= HIGH_STANDARD_NUM ? HIGH_ACTIVE_CHART_COLOR : ACTIVE_CHART_COLOR)
    }
  }

  const ToolTip = ({ value }) => {
    console.log(value, value)
    return <div className={GRAPH_TOOLTIP_CLS}>{t(s_caf.fluency.tooltip1(value))}</div>
  }

  return (
    <>
      <div ref={scrollRef} className="no-scrollbar relative h-[240px] w-full overflow-x-scroll">
        <Bar
          data={graphData}
          indexBy={'turn'}
          keys={[dataKey]}
          gridYValues={yRange}
          // 데이터 값 표시
          enableLabel={false}
          // 그래프 z-index 순서
          layers={['grid', 'bars', 'axes', 'markers']}
          markers={[
            {
              axis: 'y',
              value: 4,
              lineStyle: { stroke: 'red', strokeWidth: type !== CAF_ANALYSIS_TYPE_MAP[3] ? 1 : 0 },
            },
          ]}
          valueScale={{ type: 'linear' }}
          // y축 line
          theme={graph_common_props}
          colors={({ data }) => data.color}
          colorBy={'indexValue'}
          width={chartWidth}
          margin={{ bottom: 40, left: 30, top: 20 }}
          borderRadius={2}
          padding={0.3}
          height={240}
          ariaLabel="bar-chart"
          role="application"
          layout="vertical"
          axisLeft={{
            tickSize: 0,
            tickValues: yRange,
            tickPadding: 10,
            renderTick: (tick: AxisTickProps<string>) => (
              <CustomYTick tick={tick} scrollX={scrollX} xGap={-10} yGap={5} />
            ),
          }}
          axisBottom={{
            tickSize: 0,
            legendPosition: 'start',
            tickValues: xRange,
            renderTick: (tick: AxisTickProps<string>) => <CustomXTick tick={tick} yGap={17} />,
          }}
          tooltip={ToolTip}
          onClick={(e: ComputedDatum<{ mlr?: number; turn: number; color: string }>) => handleClickBarItem(e)}
          onMouseLeave={() =>
            rollbackChartColor({
              data: graphData,
              setData: setGraphData,
              callback: (item) => setChartColor(item),
            })
          }
        />
      </div>
      <div className="text-h5 flex items-center justify-center gap-[6px] text-center font-medium text-gray-500">
        {t(s_caf.graph_common.user_section.title)}
        {isMobile ? (
          <img
            src={ICON_QUESTION_GRAY}
            width={16}
            height={16}
            onClick={handleClickTooltip}
            alt="question"
            className="mb-4"
          />
        ) : (
          <DSTooltip
            place="bottom"
            isMultiline={true}
            isOpenOnClick={true}
            triggerComponent={
              <QIcon_question_outline fill={COLORS.GRAY600} width={16} height={16} className="mb-[1px]" />
            }
            contentComponent={t(s_caf.graph_common.user_section.modal_subtitle)}
          />
        )}
      </div>
    </>
  )
}

export default BarChart
